import { useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  createStockScheduleCommands,
  stockScheduleRequest,
  type StockScheduleAction,
} from "../../../../packages/shared/stock-schedule/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
export type StockScheduleData = {
  connection: { id: string; domain: string; connected: number } | null;
  schedule: {
    connection_id: string;
    domain: string;
    enabled: number;
    revision: number;
    interval_minutes: number;
    next_run_at: string | null;
    last_error: string | null;
    last_checked_at: string | null;
  } | null;
};
type Preview = {
  ready: boolean;
  reasons: string[];
  preview_hash: string;
  connection_id: string;
  store: string;
  product_count: number;
  warehouse_count: number;
  location_count: number;
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com",
  commands = new Map<string, ReturnType<typeof createStockScheduleCommands>>();
const storage = {
  get length() {
    return localStorage.length;
  },
  key: (i: number) => localStorage.key(i),
  getItem: (k: string) => localStorage.getItem(k),
  setItem: (k: string, v: string) => localStorage.setItem(k, v),
  removeItem: (k: string) => localStorage.removeItem(k),
};
export function StockScheduleControls({
  account,
  data,
  canSchedule,
  onSaved,
}: {
  account: QueueAccount;
  data: StockScheduleData;
  canSchedule: boolean;
  onSaved: () => void;
}) {
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createStockScheduleCommands(base, account, storage));
  const command = commands.get(key)!,
    state = useSyncExternalStore(command.subscribe, command.snapshot);
  const [configure, setConfigure] = useState(false),
    [enabled, setEnabled] = useState(false),
    [interval, setInterval] = useState("60"),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [review, setReview] = useState<{
      action: StockScheduleAction;
      preview: Preview | null;
    } | null>(null);
  const locked = !canSchedule || state.busy || !!state.pending;
  const prepare = async () => {
    setError("");
    setLoading(true);
    try {
      const preview = enabled
        ? await stockScheduleRequest<Preview>(base, account, "/preview?connection_id=" + encodeURIComponent(data.connection?.id || data.schedule?.connection_id || ""))
        : null;
      if (preview && !preview.ready) throw new Error(preview.reasons.join(" "));
      const connection_id =
          preview?.connection_id ||
          data.schedule?.connection_id ||
          data.connection?.id,
        domain =
          preview?.store || data.schedule?.domain || data.connection?.domain;
      if (!connection_id || !domain)
        throw new Error(
          "Connect a Shopify store before configuring a schedule.",
        );
      setReview({
        action: {
          connection_id,
          domain,
          revision: data.schedule?.revision || 0,
          enabled,
          interval_minutes: Number(interval),
          ...(preview ? { preview_hash: preview.preview_hash } : {}),
        },
        preview,
      });
      setConfigure(false);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "The stock schedule could not be reviewed.",
      );
    } finally {
      setLoading(false);
    }
  };
  const send = async () => {
    if (!review) return;
    const action = review.action;
    setReview(null);
    await command.submit(action);
    if (command.snapshot().success) onSaved();
  };
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Automatic stock publication
        </Text>
        <Text as="p">
          Store: {data.connection?.domain || data.schedule?.domain || "Choose a store"}.
        </Text>
        <Text as="p">
          Publish current available warehouse stock to Shopify at a chosen
          interval, including zero-stock products. Physical warehouse stock
          stays unchanged.
        </Text>
        <Text as="p">
          {data.schedule?.enabled
            ? `Enabled every ${data.schedule.interval_minutes} minutes. Next check: ${data.schedule.next_run_at || "pending"} UTC.`
            : "Automatic stock publication is off."}
        </Text>
        {data.schedule?.last_error && (
          <Banner tone="warning">{data.schedule.last_error}</Banner>
        )}
        {!data.connection?.connected && (
          <Banner tone="warning">
            Connect a Shopify store before enabling automatic stock publication.
          </Banner>
        )}
        {state.error && <Banner tone="critical">{state.error}</Banner>}
        {state.success && <Banner tone="success">{state.success}</Banner>}
        {state.pending && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p">
                A saved schedule change for {JSON.parse(state.pending.body).domain} needs a confirmed reply. It stays saved
                after you close the browser.
              </Text>
              <Button
                loading={state.busy}
                disabled={!canSchedule}
                onClick={() =>
                  void command.retry().then(() => {
                    if (command.snapshot().success) onSaved();
                  })
                }
              >
                Retry saved schedule
              </Button>
            </BlockStack>
          </Banner>
        )}
        <Text as="p">
          A new run waits for this store’s previous scheduled updates to finish. Failed,
          skipped or missing updates need review. View a run below to see each
          product result.
        </Text>
        <Button
          disabled={locked || (!data.connection && !data.schedule)}
          onClick={() => {
            setEnabled(!!data.schedule?.enabled);
            setInterval(String(data.schedule?.interval_minutes || 60));
            setError("");
            setConfigure(true);
          }}
        >
          Configure stock schedule
        </Button>
        <Modal
          open={configure}
          title="Stock publication schedule"
          onClose={() => {
            if (!loading) setConfigure(false);
          }}
          primaryAction={{
            content: "Review schedule",
            disabled: locked,
            loading,
            onAction: () => void prepare(),
          }}
          secondaryActions={[
            {
              content: "Cancel",
              disabled: loading,
              onAction: () => setConfigure(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              {error && <Banner tone="critical">{error}</Banner>}
              <Checkbox
                label="Enable automatic stock publication"
                checked={enabled}
                disabled={loading}
                onChange={setEnabled}
              />
              <Select
                label="Publication interval"
                value={interval}
                disabled={loading}
                options={[15, 30, 60, 120, 240, 360, 720, 1440].map((v) => ({
                  label:
                    v < 60
                      ? `Every ${v} minutes`
                      : v === 60
                        ? "Every hour"
                        : `Every ${v / 60} hours`,
                  value: String(v),
                }))}
                onChange={setInterval}
              />
              <Text as="p">
                This uses your current company access. Disabling stops future
                runs. Already queued updates continue and keep their results.
              </Text>
              <Text as="p">
                Saving an enabled schedule starts a fresh schedule after the
                chosen interval. Older failed runs remain in history. Pending
                older runs must still finish.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
        <Modal
          open={!!review}
          title="Review stock schedule"
          onClose={() => setReview(null)}
          primaryAction={{
            content: "Confirm stock schedule",
            disabled: locked,
            onAction: () => void send(),
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setReview(null) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="p">Store: {review?.action.domain}</Text>
              <Text as="p">
                {review?.action.enabled
                  ? `Enable publication every ${review.action.interval_minutes} minutes.`
                  : "Disable future scheduled stock publications."}
              </Text>
              {review?.preview && (
                <Text as="p">
                  Currently {review.preview.product_count} mapped products
                  across {review.preview.warehouse_count} warehouses and{" "}
                  {review.preview.location_count} Shopify locations. Each run
                  includes all products and warehouses mapped at that time,
                  including new mappings.
                </Text>
              )}
              <Text as="p">
                Each update sends current available stock after reservations.
                Zero clears available Shopify stock. Saved runs are queued work;
                their results show what Shopify confirmed.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Card>
  );
}
